import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  onAuthStateChanged,
  signOut,
  signInWithCustomToken,
  User as FirebaseUser,
} from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions, APP_ID } from "@/lib/firebase";
import {
  registerFCMToken,
  deleteFCMToken,
  cancelAllLocalNotifications,
} from "@/services/notificationService";
import type { User, AuthContextType, ApprovedUser } from "@/types";
import { logger } from "@/lib/logger";
import { populateUserCache } from "@/data/mockData";
import { getAllActiveUsers } from "@/services/userService";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ROLE_ALIAS: Record<string, string> = {
  operation_user: "operations_user",
  operations: "operations_user",
};

function normalizeRole(role: unknown): string | null {
  if (typeof role !== "string") return null;
  const normalized = role
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (!normalized) return null;
  return ROLE_ALIAS[normalized] || normalized;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pendingPhoneRef = useRef<string>("");
  const signalUnsubRef = useRef<(() => void) | null>(null);

  // ── Map ApprovedUser document → User type ──────────────────────────
  const mapApprovedUserToProfile = (
    documentId: string,
    data: ApprovedUser,
    phone?: string,
  ): User => {
    const rolesFromArray = Array.isArray(data.roles)
      ? data.roles
          .map((role) => normalizeRole(role))
          .filter((role): role is string => !!role)
      : [];

    const primaryRole =
      normalizeRole(data.primaryRole) ||
      normalizeRole((data as unknown as { role?: unknown }).role) ||
      rolesFromArray[0];

    const mergedRoles = Array.from(
      new Set([...(primaryRole ? [primaryRole] : []), ...rolesFromArray]),
    );

    return {
      id: data.userId,
      documentId,
      userId: data.userId,
      phone: data.phone || phone || "",
      officeId: data.officeId,
      name: data.name,
      email: data.email || "",
      roles: mergedRoles,
      primaryRole,
      monthlyTarget: data.monthlyTarget || 0,
      monthlyTargetCount: data.monthlyTargetCount || 0,
      loginTarget: data.loginTarget || 0,
      loginTargetCount: data.loginTargetCount || 0,
      currentDisbursement: 0,
      currentDisbursementCount: 0,
      currentLogin: 0,
      currentLoginCount: 0,
      commission: 0,
    };
  };

  // ── Fetch profile by dsaUserId (from custom claims) ────────────────
  const fetchUserProfileById = async (
    dsaUserId: string,
  ): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, "approvedUsers", dsaUserId));
      if (!userDoc.exists()) {
        throw new Error("User not authorized. Contact administrator.");
      }

      const data = userDoc.data() as ApprovedUser;
      if (!data.isActive) {
        throw new Error(
          "Your account has been deactivated. Contact administrator.",
        );
      }

      return mapApprovedUserToProfile(userDoc.id, data);
    } catch (err) {
      logger.error("Failed to fetch user profile", err, { dsaUserId });
      throw err;
    }
  };

  // ── Send OTP (server-side via Cloud Function) ──────────────────────
  const sendOTP = async (phone: string) => {
    try {
      setError(null);

      const cleanPhone = phone.replace(/[\s\-()]/g, "");
      const fullPhone = cleanPhone.startsWith("+91")
        ? cleanPhone
        : `+91${cleanPhone}`;

      const sendOTPFn = httpsCallable<
        { phone: string; application: string },
        { success: boolean; message: string }
      >(functions, "sendOTP");
      const result = await sendOTPFn({
        phone: fullPhone,
        application: APP_ID,
      });

      if (!result.data?.success) {
        throw new Error(result.data?.message || "Failed to send OTP");
      }

      pendingPhoneRef.current = fullPhone;
      logger.info("OTP sent successfully", { phone: fullPhone });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to send OTP";
      logger.error("Failed to send OTP", err, { phone });
      setError(msg);
      throw err;
    }
  };

  // ── Verify OTP (server-side) → sign in with custom token ──────────
  const verifyOTP = async (otp: string) => {
    try {
      setError(null);
      setLoading(true);

      if (!pendingPhoneRef.current) {
        throw new Error("No OTP session found. Please request OTP again.");
      }

      type VerifyOTPResponse = {
        success: boolean;
        message?: string;
        token: string;
        user: {
          id: string;
          name: string;
          phone: string;
          role: string;
          roles: string[];
          officeId: string;
        };
      };
      const verifyOTPFn = httpsCallable<
        { phone: string; otp: string; application: string },
        VerifyOTPResponse
      >(functions, "verifyOTP");
      const result = await verifyOTPFn({
        phone: pendingPhoneRef.current,
        otp,
        application: APP_ID,
      });

      if (!result.data?.success || !result.data?.token) {
        throw new Error(result.data?.message || "Verification failed");
      }

      // Sign in with the custom token minted by the server
      await signInWithCustomToken(auth, result.data.token);

      // Force refresh to get custom claims in ID token
      const currentUser = auth.currentUser;
      if (currentUser) {
        await currentUser.getIdToken(true); // Force refresh
      }

      // Fetch full profile – the onAuthStateChanged listener will also
      // fire, but we set user here immediately for a snappier UX.
      const profile = await fetchUserProfileById(result.data.user.id);
      setUser(profile);
      pendingPhoneRef.current = "";

      // Register FCM token (fire-and-forget)
      if (profile) {
        logger.info("User logged in successfully", {
          userId: profile.id,
          phone: profile.phone,
          role: profile.primaryRole,
        });
        logger.setUserContext(profile.id, {
          role: profile.primaryRole,
          officeId: profile.officeId,
        });
        registerFCMToken(profile.id).catch((e) =>
          logger.warn("Failed to register FCM token", e, {
            userId: profile.id,
          }),
        );
      }

      setLoading(false);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Invalid OTP. Please try again.";
      logger.error("Failed to verify OTP", err, {
        phone: pendingPhoneRef.current,
      });
      setError(msg);
      setLoading(false);
      throw err;
    }
  };

  // ── Logout ─────────────────────────────────────────────────────────
  const logout = async () => {
    try {
      const userId = user?.id;
      if (user) {
        await deleteFCMToken(user.id).catch((e) =>
          logger.warn("Failed to delete FCM token", e, { userId: user.id }),
        );
        // Cancel all scheduled local notifications so no reminders fire after logout
        await cancelAllLocalNotifications().catch((e) =>
          logger.warn("Failed to cancel local notifications on logout", e, {
            userId: user.id,
          }),
        );
      }

      await signOut(auth);
      // Clean up legacy localStorage keys from previous auth model
      localStorage.removeItem("verified_phone");
      setUser(null);
      pendingPhoneRef.current = "";
      setError(null);
      logger.info("User logged out successfully", { userId });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Logout failed";
      logger.error("Failed to logout", err, { userId: user?.id });
      setError(msg);
      throw err;
    }
  };

  // ── Auth-state listener (cold start & token refresh) ───────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser: FirebaseUser | null) => {
        if (firebaseUser) {
          try {
            // Read dsaUserId from custom claims
            const tokenResult = await firebaseUser.getIdTokenResult();
            const dsaUserId = tokenResult.claims.dsaUserId as
              | string
              | undefined;

            if (dsaUserId) {
              const profile = await fetchUserProfileById(dsaUserId);
              setUser(profile);

              // Populate user name cache for LeadCard to display names
              getAllActiveUsers()
                .then((users) => populateUserCache(users))
                .catch(() => {
                  /* non-critical */
                });

              if (profile) {
                registerFCMToken(profile.id).catch((e) =>
                  logger.warn("Failed to register FCM token on startup", e, {
                    userId: profile.id,
                  }),
                );
              }

              // ── Listen for claims-updated signal from the backend ──────
              // When an admin changes a user's role/status, syncCustomClaims
              // writes to userSignals/{firebaseUid}. We detect that here and
              // force-refresh the ID token so new claims apply immediately.
              if (signalUnsubRef.current) signalUnsubRef.current();
              let isInitialSignal = true;
              signalUnsubRef.current = onSnapshot(
                doc(db, "userSignals", firebaseUser.uid),
                async (snap) => {
                  // Skip the first emit (initial state)
                  if (isInitialSignal) {
                    isInitialSignal = false;
                    return;
                  }
                  if (!snap.exists()) return;
                  try {
                    logger.info(
                      "Claims update signal received — refreshing token",
                      { firebaseUid: firebaseUser.uid },
                    );
                    await firebaseUser.getIdToken(true); // force-refresh
                    const refreshed = await firebaseUser.getIdTokenResult();
                    const refreshedDsaUserId = refreshed.claims.dsaUserId as
                      | string
                      | undefined;
                    if (refreshedDsaUserId) {
                      const refreshedProfile =
                        await fetchUserProfileById(refreshedDsaUserId);
                      setUser(refreshedProfile);
                      logger.info("Profile refreshed after claims update", {
                        dsaUserId: refreshedDsaUserId,
                      });
                    }
                  } catch (refreshErr) {
                    logger.warn(
                      "Failed to refresh profile after claims update",
                      refreshErr,
                      { firebaseUid: firebaseUser.uid },
                    );
                  }
                },
                (err) =>
                  logger.warn("userSignals listener error", err, {
                    firebaseUid: firebaseUser.uid,
                  }),
              );
            } else {
              // Legacy anonymous session without claims – sign out
              logger.warn(
                "Auth user has no dsaUserId claim - signing out",
                undefined,
                { firebaseUid: firebaseUser.uid },
              );
              if (signalUnsubRef.current) {
                signalUnsubRef.current();
                signalUnsubRef.current = null;
              }
              await signOut(auth);
              localStorage.removeItem("verified_phone");
              setUser(null);
            }
          } catch (err: unknown) {
            logger.error("Failed to restore session", err, {
              firebaseUid: firebaseUser?.uid,
            });
            setError(
              err instanceof Error ? err.message : "Session restore failed",
            );
            if (signalUnsubRef.current) {
              signalUnsubRef.current();
              signalUnsubRef.current = null;
            }
            await signOut(auth);
            localStorage.removeItem("verified_phone");
            setUser(null);
          }
        } else {
          if (signalUnsubRef.current) {
            signalUnsubRef.current();
            signalUnsubRef.current = null;
          }
          setUser(null);
        }
        setLoading(false);
      },
    );

    return () => {
      unsubscribe();
      if (signalUnsubRef.current) {
        signalUnsubRef.current();
        signalUnsubRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value: AuthContextType = {
    user,
    loading,
    error,
    sendOTP,
    verifyOTP,
    logout,
    setConfirmationResult: () => {}, // vestigial no-op, kept for compatibility
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
